import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Grid,
  Card,
  CardContent,
  Typography,
  Button,
  Avatar,
  IconButton,
  Drawer,
  List,
  ListItemIcon,
  ListItemText,
  ListItemButton,
  AppBar,
  Toolbar,
  Badge,
  Divider,
  Chip,
} from '@mui/material';
import {
  Dashboard as DashboardIcon,
  Inventory,
  Assignment,
  Assessment,
  People,
  Settings,
  Logout,
  Notifications,
  Menu as MenuIcon,
  TrendingUp,
  Warning,
  CheckCircle,
  Schedule,
  LocalHospital,
  HealthAndSafety,
} from '@mui/icons-material';
import { motion } from 'framer-motion';
import { styled } from '@mui/material/styles';
import RegistrosSanitarios from './RegistrosSanitarios';
import AdminPanel from './AdminPanel';
import Productos from './Productos';
import Farmacovigilancia from './Farmacovigilancia';

const drawerWidth = 280;

const StyledDrawer = styled(Drawer)(({ theme }) => ({
  width: drawerWidth,
  flexShrink: 0,
  '& .MuiDrawer-paper': {
    width: drawerWidth,
    boxSizing: 'border-box',
    background: 'linear-gradient(180deg, #1e293b 0%, #0f172a 100%)',
    color: 'white',
    borderRight: 'none',
  },
}));

const StyledAppBar = styled(AppBar)(({ theme }) => ({
  background: 'rgba(255, 255, 255, 0.8)',
  backdropFilter: 'blur(20px)',
  boxShadow: '0 4px 20px rgba(0, 0, 0, 0.05)',
  color: theme.palette.text.primary,
}));

const GradientCard = styled(Card)(({ theme, gradient }) => ({
  background: gradient || 'linear-gradient(135deg, #2196f3 0%, #1976d2 100%)',
  color: 'white',
  borderRadius: '20px',
  boxShadow: '0 10px 30px rgba(0, 0, 0, 0.15)',
  transition: 'transform 0.3s ease, box-shadow 0.3s ease',
  '&:hover': {
    transform: 'translateY(-5px)',
    boxShadow: '0 15px 40px rgba(0, 0, 0, 0.2)',
  },
}));

const GlassCard = styled(Card)(({ theme }) => ({
  background: 'rgba(255, 255, 255, 0.7)',
  backdropFilter: 'blur(20px)',
  borderRadius: '20px',
  border: '1px solid rgba(255, 255, 255, 0.3)',
  boxShadow: '0 8px 32px rgba(0, 0, 0, 0.1)',
  transition: 'all 0.3s ease',
  '&:hover': {
    transform: 'translateY(-3px)',
    boxShadow: '0 12px 40px rgba(0, 0, 0, 0.15)',
  },
}));

const StatsCard = ({ icon: Icon, title, value, change, color, gradient }) => (
  <motion.div
    initial={{ opacity: 0, y: 20 }}
    animate={{ opacity: 1, y: 0 }}
    transition={{ duration: 0.5 }}
    whileHover={{ y: -5 }}
  >
    <GradientCard gradient={gradient}>
      <CardContent sx={{ p: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
          <Avatar sx={{ bgcolor: 'rgba(255, 255, 255, 0.2)', width: 56, height: 56 }}>
            <Icon sx={{ fontSize: 28 }} />
          </Avatar>
          {change && (
            <Chip
              icon={<TrendingUp sx={{ fontSize: 16 }} />}
              label={change}
              size="small"
              sx={{
                bgcolor: 'rgba(255, 255, 255, 0.2)',
                color: 'white',
                fontWeight: 600,
              }}
            />
          )}
        </Box>
        <Typography variant="h3" component="div" sx={{ fontWeight: 700, mb: 1 }}>
          {value}
        </Typography>
        <Typography variant="body1" sx={{ opacity: 0.9 }}>
          {title}
        </Typography>
      </CardContent>
    </GradientCard>
  </motion.div>
);

const ActivityCard = () => (
  <GlassCard>
    <CardContent sx={{ p: 3 }}>
      <Typography variant="h6" gutterBottom sx={{ fontWeight: 600, mb: 3 }}>
        📊 Actividad Reciente
      </Typography>
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        {[
          { icon: <CheckCircle color="success" />, text: 'Nuevo producto registrado', time: 'Hace 2 horas' },
          { icon: <Assignment color="primary" />, text: 'Auditoría completada', time: 'Hace 5 horas' },
          { icon: <Warning color="warning" />, text: 'Registro por vencer', time: 'Hace 1 día' },
          { icon: <Schedule color="info" />, text: 'Reunión programada', time: 'Hace 2 días' },
        ].map((item, index) => (
          <Box
            key={index}
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 2,
              p: 2,
              borderRadius: '12px',
              bgcolor: 'rgba(0, 0, 0, 0.02)',
              transition: 'all 0.3s',
              '&:hover': {
                bgcolor: 'rgba(59, 130, 246, 0.05)',
                transform: 'translateX(5px)',
              },
            }}
          >
            {item.icon}
            <Box sx={{ flex: 1 }}>
              <Typography variant="body2" sx={{ fontWeight: 500 }}>
                {item.text}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {item.time}
              </Typography>
            </Box>
          </Box>
        ))}
      </Box>
    </CardContent>
  </GlassCard>
);

const QuickActionsCard = () => (
  <GlassCard>
    <CardContent sx={{ p: 3 }}>
      <Typography variant="h6" gutterBottom sx={{ fontWeight: 600, mb: 3 }}>
        🚀 Acciones Rápidas
      </Typography>
      <Grid container spacing={2}>
        {[
          { icon: <Inventory />, label: 'Nuevo Producto', color: '#2196f3' },
          { icon: <Assignment />, label: 'Registro Sanitario', color: '#42a5f5' },
          { icon: <Assessment />, label: 'Nueva Auditoría', color: '#66bb6a' },
          { icon: <People />, label: 'Agregar Usuario', color: '#ff9800' },
        ].map((action, index) => (
          <Grid item xs={6} key={index}>
            <Button
              fullWidth
              variant="outlined"
              startIcon={action.icon}
              sx={{
                py: 2,
                borderRadius: '12px',
                borderColor: 'rgba(0, 0, 0, 0.1)',
                color: action.color,
                textTransform: 'none',
                fontWeight: 500,
                '&:hover': {
                  borderColor: action.color,
                  bgcolor: `${action.color}10`,
                  transform: 'scale(1.02)',
                },
                transition: 'all 0.3s',
              }}
            >
              {action.label}
            </Button>
          </Grid>
        ))}
      </Grid>
    </CardContent>
  </GlassCard>
);

const Dashboard = ({ user, onLogout }) => {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [selectedMenu, setSelectedMenu] = useState('dashboard');
  const [stats, setStats] = useState({
    productos: 0,
    registros: 0,
    auditorias: 0,
    usuarios: 1,
  });
  const [systemConfig, setSystemConfig] = useState({
    system_logo: null,
    system_name: 'Plataforma Regulatoria',
    system_subtitle: 'Farmacéutica',
  });

  useEffect(() => {
    // Cargar estadísticas desde el API
    const loadStats = async () => {
      try {
        const token = localStorage.getItem('token');

        const response = await fetch(`/api/dashboard/stats`, {
          headers: {
            'Authorization': `Bearer ${token}`,
          },
        });

        if (response.ok) {
          const data = await response.json();
          setStats({
            productos: data.totalProductos || 0,
            registros: data.totalRegistros || 0,
            auditorias: data.totalAuditorias || 0,
            usuarios: 1,
          });
        }
      } catch (error) {
        console.error('Error loading stats:', error);
      }
    };

    // Cargar configuración del sistema
    const loadSystemConfig = async () => {
      try {
        const response = await fetch(`/api/system-config`);

        if (response.ok) {
          const data = await response.json();
          setSystemConfig({
            system_logo: data.system_logo || null,
            system_name: data.system_name || 'Plataforma Regulatoria',
            system_subtitle: data.system_subtitle || 'Farmacéutica',
          });
        }
      } catch (error) {
        console.error('Error loading system config:', error);
      }
    };

    loadStats();
    loadSystemConfig();
  }, []);

  const menuItems = [
    { id: 'dashboard', label: 'Dashboard', icon: <DashboardIcon /> },
    { id: 'registros', label: 'Registros Sanitarios', icon: <Assignment /> },
    { id: 'productos', label: 'Productos', icon: <Inventory /> },
    { id: 'xentury', label: 'Documentos Xentury Global', icon: <Assessment /> },
    { id: 'consult', label: 'Archivos Consult Pharma', icon: <Assessment /> },
    // Biblioteca de Farmacovigilancia: el invitado no la ve (los reportes traen datos de pacientes)
    ...(user?.role === 'guest' ? [] : [{ id: 'farmacovigilancia', label: 'Farmacovigilancia', icon: <HealthAndSafety /> }]),
  ];

  // Agregar menú de administración solo para usuarios admin
  const adminMenuItems = user?.permissions?.includes('admin')
    ? [{ id: 'admin', label: 'Administración', icon: <Settings /> }]
    : [];

  const allMenuItems = [...menuItems, ...adminMenuItems];

  const drawer = (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      <Box sx={{ p: 3, textAlign: 'center' }}>
        <Avatar
          sx={{
            width: 80,
            height: 80,
            mx: 'auto',
            mb: 2,
            bgcolor: '#ffffff',
            border: '3px solid rgba(255, 255, 255, 0.3)',
            boxShadow: '0 8px 32px rgba(0, 0, 0, 0.2)',
          }}
        >
          {systemConfig.system_logo ? (
            <Box
              component="img"
              src={systemConfig.system_logo}
              alt="Logo"
              sx={{
                maxWidth: '100%',
                maxHeight: '100%',
                objectFit: 'contain',
              }}
            />
          ) : (
            <LocalHospital sx={{ fontSize: 40, color: '#1e3c72' }} />
          )}
        </Avatar>
        <Typography variant="h6" sx={{ fontWeight: 700 }}>
          {systemConfig.system_name}
        </Typography>
        <Typography variant="caption" sx={{ opacity: 0.7 }}>
          {systemConfig.system_subtitle}
        </Typography>
      </Box>

      <Divider sx={{ borderColor: 'rgba(255, 255, 255, 0.1)' }} />

      <Box sx={{ p: 2, flex: 1, overflowY: 'auto' }}>
        <List>
          {allMenuItems.map((item) => (
            <ListItemButton
              key={item.id}
              selected={selectedMenu === item.id}
              onClick={() => setSelectedMenu(item.id)}
              sx={{
                borderRadius: '12px',
                mb: 1,
                '&.Mui-selected': {
                  bgcolor: 'rgba(255, 255, 255, 0.1)',
                  '&:hover': {
                    bgcolor: 'rgba(255, 255, 255, 0.15)',
                  },
                },
                '&:hover': {
                  bgcolor: 'rgba(255, 255, 255, 0.05)',
                },
              }}
            >
              <ListItemIcon sx={{ color: 'white', minWidth: 40 }}>
                {item.icon}
              </ListItemIcon>
              <ListItemText
                primary={item.label}
                primaryTypographyProps={{
                  fontSize: '0.95rem',
                  fontWeight: selectedMenu === item.id ? 600 : 400,
                }}
              />
            </ListItemButton>
          ))}
        </List>
      </Box>

      <Divider sx={{ borderColor: 'rgba(255, 255, 255, 0.1)' }} />

      <Box sx={{ p: 2 }}>
        <Box
          sx={{
            p: 2,
            borderRadius: '12px',
            bgcolor: 'rgba(255, 255, 255, 0.05)',
            mb: 2,
          }}
        >
          <Typography variant="caption" sx={{ opacity: 0.7 }}>
            Usuario actual
          </Typography>
          <Typography variant="body2" sx={{ fontWeight: 600 }}>
            {user?.username || 'Usuario'}
          </Typography>
          <Chip
            label={user?.role || 'user'}
            size="small"
            sx={{
              mt: 1,
              bgcolor: 'rgba(255, 255, 255, 0.1)',
              color: 'white',
              fontSize: '0.7rem',
            }}
          />
        </Box>

        <Button
          fullWidth
          variant="outlined"
          startIcon={<Logout />}
          onClick={onLogout}
          sx={{
            borderColor: 'rgba(255, 255, 255, 0.2)',
            color: 'white',
            borderRadius: '12px',
            '&:hover': {
              borderColor: 'rgba(255, 255, 255, 0.4)',
              bgcolor: 'rgba(255, 255, 255, 0.05)',
            },
          }}
        >
          Cerrar Sesión
        </Button>
      </Box>
    </Box>
  );

  return (
    <Box sx={{ display: 'flex', minHeight: '100vh', bgcolor: '#f8fafc' }}>
      <StyledDrawer
        variant="permanent"
        sx={{
          display: { xs: 'none', md: 'block' },
        }}
      >
        {drawer}
      </StyledDrawer>

      {/* minWidth 0: sin esto una tabla ancha estira toda la página en el celular */}
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <StyledAppBar position="sticky" elevation={0}>
          <Toolbar>
            <IconButton
              edge="start"
              onClick={() => setMobileOpen(!mobileOpen)}
              sx={{ mr: 2, display: { md: 'none' } }}
            >
              <MenuIcon />
            </IconButton>

            <Typography variant="h6" sx={{ flexGrow: 1, fontWeight: 600 }}>
              {allMenuItems.find((item) => item.id === selectedMenu)?.label || 'Dashboard'}
            </Typography>

            <IconButton>
              <Badge badgeContent={3} color="error">
                <Notifications />
              </Badge>
            </IconButton>

            <Avatar
              sx={{
                ml: 2,
                bgcolor: 'primary.main',
                cursor: 'pointer',
              }}
            >
              {user?.username?.charAt(0).toUpperCase() || 'U'}
            </Avatar>
          </Toolbar>
        </StyledAppBar>

        <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
          >
            {selectedMenu === 'dashboard' && (
              <Grid container spacing={3}>
                {/* Estadísticas */}
                <Grid item xs={12} sm={6} md={3}>
                  <StatsCard
                    icon={Inventory}
                    title="Productos"
                    value={stats.productos}
                    change="+12%"
                    gradient="linear-gradient(135deg, #42a5f5 0%, #1e88e5 100%)"
                  />
                </Grid>

                <Grid item xs={12} sm={6} md={3}>
                  <StatsCard
                    icon={Assignment}
                    title="Registros Sanitarios"
                    value={stats.registros}
                    change="+8%"
                    gradient="linear-gradient(135deg, #90caf9 0%, #64b5f6 100%)"
                  />
                </Grid>

                <Grid item xs={12} sm={6} md={3}>
                  <StatsCard
                    icon={Assessment}
                    title="Auditorías"
                    value={stats.auditorias}
                    change="+23%"
                    gradient="linear-gradient(135deg, #81c784 0%, #66bb6a 100%)"
                  />
                </Grid>

                <Grid item xs={12} sm={6} md={3}>
                  <StatsCard
                    icon={People}
                    title="Usuarios Activos"
                    value={stats.usuarios}
                    gradient="linear-gradient(135deg, #ffa726 0%, #ff9800 100%)"
                  />
                </Grid>

                {/* Actividad Reciente */}
                <Grid item xs={12} md={8}>
                  <ActivityCard />
                </Grid>

                {/* Acciones Rápidas */}
                <Grid item xs={12} md={4}>
                  <QuickActionsCard />
                </Grid>
              </Grid>
            )}

            {selectedMenu === 'registros' && <RegistrosSanitarios />}

            {selectedMenu === 'productos' && <Productos />}

            {selectedMenu === 'xentury' && (
              <Box sx={{ textAlign: 'center', py: 8 }}>
                <Typography variant="h5" color="text.secondary">
                  Documentos Xentury Global (Próximamente)
                </Typography>
              </Box>
            )}

            {selectedMenu === 'consult' && (
              <Box sx={{ textAlign: 'center', py: 8 }}>
                <Typography variant="h5" color="text.secondary">
                  Archivos Consult Pharma (Próximamente)
                </Typography>
              </Box>
            )}

            {selectedMenu === 'farmacovigilancia' && <Farmacovigilancia />}

            {selectedMenu === 'admin' && <AdminPanel />}
          </motion.div>
        </Container>
      </Box>

      <Drawer
        variant="temporary"
        open={mobileOpen}
        onClose={() => setMobileOpen(false)}
        ModalProps={{ keepMounted: true }}
        sx={{
          display: { xs: 'block', md: 'none' },
          '& .MuiDrawer-paper': {
            width: drawerWidth,
            background: 'linear-gradient(180deg, #1e293b 0%, #0f172a 100%)',
            color: 'white',
          },
        }}
      >
        {drawer}
      </Drawer>
    </Box>
  );
};

export default Dashboard;
